import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot='tooltip-provider'
      delayDuration={delayDuration}
      {...props}
    />
  );
}

const Tooltip = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof TooltipPrimitive.Root> & {
    delayDuration?: number;
  }
>(({ delayDuration = 0, children, ...props }, ref) => {
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <div ref={ref}>
        <TooltipPrimitive.Root data-slot='tooltip' {...props}>
          {children}
        </TooltipPrimitive.Root>
      </div>
    </TooltipProvider>
  );
});

Tooltip.displayName = 'Tooltip';

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot='tooltip-trigger' {...props} />;
}

type TooltipContentProps = React.ComponentProps<
  typeof TooltipPrimitive.Content
> & {
  hideArrow?: boolean;
};

const TOOLTIP_CONTENT_CLASSNAME =
  'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 bg-zinc-900 text-zinc-100 dark:bg-zinc-100 dark:text-zinc-900 z-50 rounded-md px-3 py-2 text-xs shadow-lg max-w-[300px]';

const TOOLTIP_ARROW_CLASSNAME =
  'bg-zinc-900 fill-zinc-900 dark:bg-zinc-100 dark:fill-zinc-100 z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]';

function TooltipContent({
  className,
  children,
  hideArrow,
  ...props
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot='tooltip-content'
        className={cn(TOOLTIP_CONTENT_CLASSNAME, className)}
        {...props}
      >
        {children}
        {!hideArrow && (
          <TooltipPrimitive.Arrow className={TOOLTIP_ARROW_CLASSNAME} />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TOOLTIP_CONTENT_CLASSNAME,
  TOOLTIP_ARROW_CLASSNAME,
};
